import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * mindhunters — THE brand wordmark. SINGLE SOURCE OF TRUTH.
 *
 * Canon (ADR-001, founder decision 2026-07-11): the wordmark is the word
 * `mindhunters` — lowercase, Fraunces **italic 700**, high optical size for
 * edge (display, not literary), tight tracking, no swashes. One word, no
 * split, and NEVER paired with an icon.
 *
 * This component is intentionally PURE/presentational (no hooks) so it is
 * safe in server components AND in OG/satori rendering. Clickable contexts
 * (nav home button, external badge) compose it — they do NOT re-declare the
 * font. The wiring guard (tests/brand/wordmark-canon-guard.test.ts) fails CI
 * if any file renders the wordmark text with a non-Fraunces font.
 */

const SIZE_MAP = {
  sm: 13,
  md: 15,
  lg: 20,
  xl: 28,
} as const;

export type WordmarkSize = keyof typeof SIZE_MAP;

export interface WordmarkProps extends HTMLAttributes<HTMLSpanElement> {
  /** Named size (sm/md/lg/xl) or explicit px. Defaults to md. */
  size?: WordmarkSize | number;
  className?: string;
}

function resolvePx(size: WordmarkSize | number | undefined): number {
  if (typeof size === 'number') return size;
  return SIZE_MAP[size ?? 'md'];
}

export function Wordmark({ size, className, style, ...props }: WordmarkProps) {
  const px = resolvePx(size);
  return (
    <span
      className={cn('select-none', className)}
      style={{
        fontFamily: "'Fraunces', Georgia, serif",
        fontStyle: 'italic',
        fontWeight: 700,
        // High optical size (display cut) for edge — pinned, not auto, so the
        // wordmark stays sharp regardless of render size ("filo, no literaria").
        fontOpticalSizing: 'none',
        fontVariationSettings: "'opsz' 96, 'wght' 700",
        textTransform: 'lowercase',
        letterSpacing: '-0.005em',
        lineHeight: 1,
        fontSize: px,
        color: 'var(--text-primary, var(--text-1))',
        ...style,
      }}
      {...props}
    >
      mindhunters
    </span>
  );
}
